import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Box } from "@mui/material";
import { FormatBold, FormatItalic } from "@mui/icons-material";
import { useEditor, EditorContent } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import History from "@tiptap/extension-history";
import Placeholder from "@tiptap/extension-placeholder";
import { sanitizePhrase } from "../../utils/phrase-formatting";

function wrapP(html: string): string {
  if (!html) return "<p></p>";
  if (html.startsWith("<p>")) return html;
  return `<p>${html}</p>`;
}

function unwrapP(html: string): string {
  if (/^<p>(<br[^>]*>)?<\/p>$/.test(html)) return "";
  return html.replace(/^<p>([\s\S]*)<\/p>$/, "$1");
}

interface FormattableInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  id?: string;
  style?: React.CSSProperties;
  onFocus?: () => void;
  onClick?: () => void;
  spellCheck?: boolean;
  lang?: string;
}

const FormattableInput = forwardRef<HTMLDivElement, FormattableInputProps>(
  ({ value, onChange, placeholder, readOnly, id, style, onFocus, onClick, spellCheck, lang }, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const blurTimeout = useRef<ReturnType<typeof setTimeout>>();
    const [isFocused, setIsFocused] = useState(false);
    const [, forceUpdate] = useState(0);

    // Stable refs for callbacks used inside TipTap's one-time editorProps
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onFocusRef = useRef(onFocus);
    onFocusRef.current = onFocus;

    useImperativeHandle(ref, () => wrapperRef.current as HTMLDivElement);

    const editor = useEditor({
      immediatelyRender: true,
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        History,
        Placeholder.configure({ placeholder: placeholder || "" }),
      ],
      content: wrapP(value),
      editable: !readOnly,
      onUpdate({ editor }) {
        const html = unwrapP(editor.getHTML());
        onChangeRef.current(sanitizePhrase(html));
      },
      onTransaction() {
        forceUpdate((n) => n + 1);
      },
      onFocus() {
        clearTimeout(blurTimeout.current);
        setIsFocused(true);
        onFocusRef.current?.();
      },
      onBlur() {
        blurTimeout.current = setTimeout(() => setIsFocused(false), 200);
      },
      editorProps: {
        attributes: {
          spellcheck: spellCheck ? "true" : "false",
          lang: lang || "",
        },
        handleKeyDown(_view, event) {
          if (event.key === "Enter") return true;
          return false;
        },
        handlePaste(view, event) {
          event.preventDefault();
          const text = (event.clipboardData?.getData("text/plain") || "").replace(/[\r\n]+/g, " ");
          view.dispatch(view.state.tr.insertText(text));
          return true;
        },
      },
    });

    // Sync external value changes (drag reorder, case transforms, etc.)
    useEffect(() => {
      if (!editor) return;
      const cleanValue = sanitizePhrase(value || "");
      const currentContent = sanitizePhrase(unwrapP(editor.getHTML()));
      if (currentContent !== cleanValue) {
        editor.commands.setContent(wrapP(cleanValue), { emitUpdate: false });
      }
    }, [value, editor]);

    // Sync readOnly
    useEffect(() => {
      if (editor) editor.setEditable(!readOnly);
    }, [readOnly, editor]);

    // Cleanup blur timeout on unmount
    useEffect(() => () => clearTimeout(blurTimeout.current), []);

    const showToolbar = isFocused && !readOnly;
    const isBold = editor?.isActive("bold") ?? false;
    const isItalic = editor?.isActive("italic") ?? false;

    return (
      <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "stretch" }}>
        {showToolbar && (
          <Box
            sx={{
              position: "absolute",
              top: "-32px",
              left: "0",
              display: "flex",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 10,
              border: "1px solid #ddd",
              userSelect: "none",
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Box
              component="span"
              title="Bold (Ctrl+B)"
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                editor?.chain().focus().toggleBold().run();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "28px",
                height: "28px",
                cursor: "pointer",
                borderRadius: "3px",
                backgroundColor: isBold ? "rgba(0,0,0,0.12)" : "transparent",
                "&:hover": { backgroundColor: isBold ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.06)" },
              }}
            >
              <FormatBold sx={{ fontSize: "1rem" }} />
            </Box>
            <Box
              component="span"
              title="Italic (Ctrl+I)"
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                editor?.chain().focus().toggleItalic().run();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "28px",
                height: "28px",
                cursor: "pointer",
                borderRadius: "3px",
                backgroundColor: isItalic ? "rgba(0,0,0,0.12)" : "transparent",
                "&:hover": { backgroundColor: isItalic ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.06)" },
              }}
            >
              <FormatItalic sx={{ fontSize: "1rem" }} />
            </Box>
          </Box>
        )}
        <div
          ref={wrapperRef}
          id={id}
          className="formattable-tiptap"
          style={{ width: "100%", ...style }}
          onClick={onClick}
        >
          <EditorContent editor={editor} />
        </div>
        <style>{`
          .formattable-tiptap .tiptap {
            padding: 0.25rem;
            width: 100%;
            font-family: inherit;
            font-size: 1rem;
            outline: none;
            min-height: 25px;
            white-space: pre-wrap;
            word-break: break-word;
            box-sizing: border-box;
          }
          .formattable-tiptap .tiptap[contenteditable="false"] {
            cursor: not-allowed;
          }
          .formattable-tiptap .tiptap p {
            margin: 0;
          }
          .formattable-tiptap .tiptap p.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            color: #aaa;
            pointer-events: none;
            float: left;
            height: 0;
          }
        `}</style>
      </Box>
    );
  }
);

FormattableInput.displayName = "FormattableInput";

export default FormattableInput;
